import { Map } from "mapbox-gl";
import { CallbackResponse } from "scrollama";

import {
  fadeCircles,
  fadeLine,
  fadeFill,
  fadeText,
  fadeLocator
} from "./animations";

import { getSteps } from "./steps";

import {
  farmHighlightStroke,
  farmInfectedFill,
  farmInfectedStroke,
  farmNeutralFill
} from "./colors";

import { paintCircles } from "./paint";
import { updateCountyLabels } from "./layers";

export function handleSteps(
  response: CallbackResponse,
  map: Map,
  locator: HTMLDivElement,
  windowWidth: number
): void {
  const { index, direction } = response;

  const { ...mapStepConfig } = getSteps(windowWidth)[index];

  const scrollingUp = direction === "up";
  const scrollingDown = direction === "down";
  const isTinyMobile = windowWidth <= 500;

  switch (index) {
    // start with howe's hens
    case 0:
      if (scrollingDown) {
        fadeText(map, "howe-label", 1);
      }

      if (scrollingUp) {
        fadeCircles(map, "howe-point", 0);
        fadeCircles(map, "farms-base", 0);
        fadeLine(map, "farm-radius", 0);
        fadeLocator(locator, 0);
      }

      map.easeTo(mapStepConfig);
      break;

    // zoom out, show radius
    case 1:
      if (scrollingDown) {
        fadeLine(map, "farm-radius", 1);
        fadeCircles(map, "farms-base", 1);
        fadeCircles(map, "howe-point", 1);
        fadeLocator(locator, 1);

        if (isTinyMobile) {
          const mercerSource = map.getSource("Mercer-label") as any;
          if (mercerSource) {
            mercerSource.setData({
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [-84.699, 40.535333] // ~10% west
              },
              properties: {
                title: "MERCER\nCOUNTY"
              }
            });
          }
        }
      }

      if (scrollingUp) {
        fadeLine(map, "farm-radius", 1);
        fadeCircles(map, "farms-base", 1);
        fadeCircles(map, "farms-within-plume", 1);
        fadeFill(map, "plume", 0);

        const mercerSource = map.getSource("Mercer-label") as any;
        if (mercerSource) {
          mercerSource.setData({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [-84.632059, 40.535333] // Original position
            },
            properties: {
              title: "MERCER\nCOUNTY"
            }
          });
        }
      }

      map.easeTo(mapStepConfig);

      break;

    // show plume
    case 2:
      if (scrollingDown) {
        fadeLine(map, "farm-radius", 0);
        fadeCircles(map, "farms-base", 0.5);
        fadeCircles(map, "farms-within-plume", 1);

        paintCircles(
          map,
          "farms-within-plume",
          farmNeutralFill,
          farmHighlightStroke
        );

        fadeFill(map, "plume", 0.5);
      }

      if (scrollingUp) {
        fadeCircles(map, "infected-plume-farms", 0);
        fadeCircles(map, "mercer-03", 0);
        fadeCircles(map, "howe-point", 1);
        fadeText(map, "howe-label", 1);
      }

      map.easeTo(mapStepConfig);
      break;

    // show first set of infected farms
    case 3:
      if (scrollingDown) {
        fadeCircles(map, "infected-plume-farms", 1);
        fadeCircles(map, "howe-point", 0);
        fadeText(map, "howe-label", 0);

        fadeCircles(map, "mercer-03", 1);
        paintCircles(map, "mercer-03", farmInfectedFill, farmInfectedStroke);

        paintCircles(
          map,
          "infected-plume-farms",
          farmInfectedFill,
          farmInfectedStroke
        );
      }
      if (scrollingUp) {
        fadeFill(map, "plume", 0.5);
        fadeCircles(map, "farms-base", 0.5);
        fadeCircles(map, "farms-within-plume", 1);
        fadeCircles(map, "other-farms-base", 0);
        fadeCircles(map, "farms-infected", 0);
        fadeCircles(map, "howe-point", 0);

        updateCountyLabels(map, windowWidth, "adjusted");
      }

      map.easeTo(mapStepConfig);
      break;

    case 4:
      if (scrollingDown) {
        fadeFill(map, "plume", 0);

        fadeCircles(map, "farms-base", 0);
        fadeCircles(map, "farms-within-plume", 0);
        fadeCircles(map, "other-farms-base", 1);
        fadeCircles(map, "howe-point", 1);
        fadeCircles(map, "farms-infected", 1);
        fadeCircles(map, "mercer-03", 0);

        // Hide Auglaize and Van Wert county labels on small screens
        const showLabels = !isTinyMobile;
        fadeText(map, "Auglaize-label", showLabels ? 1 : 0);
        fadeText(map, "Van Wert-label", showLabels ? 1 : 0);

        updateCountyLabels(map, windowWidth, "final");

        if (isTinyMobile) {
          const mercerSource = map.getSource("Mercer-label") as any;
          if (mercerSource) {
            mercerSource.setData({
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [-84.632059, 40.535333] // Original position
              },
              properties: {
                title: "MERCER\nCOUNTY"
              }
            });
          }
        }

        paintCircles(
          map,
          "other-farms-base",
          farmInfectedFill,
          farmInfectedStroke
        );

        paintCircles(
          map,
          "farms-infected",
          farmInfectedFill,
          farmInfectedStroke
        );
      }

      if (scrollingUp) {
        updateCountyLabels(map, windowWidth, "adjusted");

        if (isTinyMobile) {
          const mercerSource = map.getSource("Mercer-label") as any;
          if (mercerSource) {
            mercerSource.setData({
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [-84.699, 40.535333] // ~10% west
              },
              properties: {
                title: "MERCER\nCOUNTY"
              }
            });
          }
        }
      }

      map.easeTo(mapStepConfig);
      break;

    default:
      break;
  }
}
